import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Crown,
  Cpu,
  BatteryCharging,
  Zap,
  Monitor,
  ArrowRight,
} from "lucide-react";
import SiteShell from "../components/SiteShell";
import Action from "../components/Action";
import SEO from "../components/SEO";

/**
 * /team — Team hub.
 *
 * Five-tile grid pointing to each department page. Visual language mirrors
 * the home OS-dashboard bento tiles (glass surface + gold hover accent).
 */

const departments = [
  {
    slug: "leadership",
    label: "Leadership",
    subtitle: "Founders and Chief Officers",
    Icon: Crown,
  },
  {
    slug: "rnd",
    label: "R&D",
    subtitle: "Engineers and IIT professors",
    Icon: Cpu,
  },
  {
    slug: "battery-rnd",
    label: "Battery R&D",
    subtitle: "Cell chemistry and pack engineering",
    Icon: BatteryCharging,
  },
  {
    slug: "solar-power",
    label: "Solar Power",
    subtitle: "Utility-scale solar EPC and O&M",
    Icon: Zap,
  },
  {
    slug: "hr-it",
    label: "HR / IT",
    subtitle: "People operations and internal systems",
    Icon: Monitor,
  },
];

const container = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.06, delayChildren: 0.15 },
  },
};

const item = {
  hidden: { opacity: 0, y: 18 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
  },
};

export default function Team() {
  return (
    <SiteShell>
      <SEO
        title="Team — Green Gold Energy"
        description="The people behind the Green Gold Grid. Sixty-plus builders across five departments — Leadership, R&D, Battery R&D, Solar Power and HR/IT."
      />

      <section
        style={{
          maxWidth: 1180,
          margin: "0 auto",
          padding:
            "clamp(48px, 7vw, 84px) clamp(20px, 5vw, 60px) clamp(30px, 5vw, 60px)",
        }}
        data-testid="team-hub"
      >
        <div className="gg-eyebrow" data-testid="team-hub-kicker">
          The Team
        </div>
        <h1 className="gg-h1" style={{ marginTop: 14 }} data-testid="team-hub-title">
          The People Behind the Green Gold Grid.
        </h1>
        <p
          style={{
            color: "var(--text-dim)",
            fontSize: 18,
            lineHeight: 1.6,
            marginTop: 18,
            maxWidth: 720,
          }}
          data-testid="team-hub-sub"
        >
          Sixty-plus builders. Five departments. One shared brain.
        </p>

        <motion.div
          variants={container}
          initial="hidden"
          animate="visible"
          className="gg-team-hub-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(5, 1fr)",
            gap: "clamp(14px, 1.6vw, 22px)",
            marginTop: 44,
          }}
          data-testid="team-hub-grid"
        >
          {departments.map(({ slug, label, subtitle, Icon }) => (
            <motion.div key={slug} variants={item}>
              <Link
                to={`/team/${slug}`}
                data-testid={`team-tile-${slug}`}
                className="gg-tile"
                style={{
                  height: "100%",
                  minHeight: 190,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  gap: 18,
                  padding: 22,
                }}
              >
                <div
                  aria-hidden
                  style={{
                    width: 42,
                    height: 42,
                    borderRadius: 12,
                    background: "rgba(244,169,31,0.14)",
                    border: "1px solid rgba(244,169,31,0.35)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "var(--gold-soft)",
                  }}
                >
                  <Icon size={20} strokeWidth={1.7} />
                </div>

                <div>
                  <h2
                    className="gg-tile-title"
                    style={{ marginBottom: 8 }}
                  >
                    {label}
                  </h2>
                  <p
                    style={{
                      color: "var(--text-dim)",
                      fontSize: 13.5,
                      lineHeight: 1.5,
                      margin: 0,
                    }}
                  >
                    {subtitle}
                  </p>
                </div>

                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    color: "var(--text-mute)",
                    fontSize: 11.5,
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                  }}
                >
                  Meet the team <ArrowRight size={12} strokeWidth={2} />
                </div>
              </Link>
            </motion.div>
          ))}
        </motion.div>

        <p
          data-testid="team-hub-join-line"
          style={{
            marginTop: 48,
            color: "var(--text-cloud)",
            fontSize: 16,
            lineHeight: 1.7,
            maxWidth: 720,
          }}
        >
          Building the founding chapter of an energy movement. If your life&rsquo;s
          work belongs here,{" "}
          <Action route="book-tour" source="team-hub">
            Schedule a visit
          </Action>
          .
        </p>
      </section>

      <style>{`
        @media (max-width: 1100px) {
          .gg-team-hub-grid {
            grid-template-columns: repeat(3, 1fr) !important;
          }
        }
        @media (max-width: 640px) {
          .gg-team-hub-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
      `}</style>
    </SiteShell>
  );
}
